import React, { useState, useRef, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { useNavigate, useParams } from "react-router-dom";
import { api, uploadImage } from "../utils/api";
import { getSocket } from "../utils/socket";
import { formatScore } from "../utils/format";
import { Avatar, vibeRingKind } from "../components/Avatar";
import { LockIcon } from "../components/messages/icons";
import { useIsMobile } from "../hooks/useIsMobile";

const TABS_KEYS = ["Chat", "Événements", "Membres", "Infos"];

const PALETTE = ["#0a0a0a", "#0a0a0a", "#0a0a0a", "#333333", "#666666", "#777777", "#4d4d4d"];
const colorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
};
const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Icons

export default function Community() {
  const { lang, user, token } = useApp();
  const { id } = useParams();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Chat");
  const [community, setCommunity] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [editingInfo, setEditingInfo] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [savingInfo, setSavingInfo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [deletingCommunity, setDeletingCommunity] = useState(false);

  const isMember = community?.my_role != null;
  const isAdmin = community?.my_role === "admin";
  const isModerator = isAdmin || community?.my_role === "moderator";
  // Un admin unique ne peut pas quitter — voir le garde-fou serveur sur POST /:id/leave (code
  // 'last_admin'). admin_names vient déjà de GET /communities/:id, pas besoin de recompter.
  const isSoleAdmin = isAdmin && (community?.admin_names?.length || 0) <= 1;

  const loadCommunity = () => {
    if (!id) return;
    api.get(`/communities/${id}`).then(data => {
      if (!data?.community) { setNotFound(true); return; }
      setCommunity(data.community);
      setEvents(data.events || []);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  };

  useEffect(() => { loadCommunity(); }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/communities/${id}/members`).then(data => setMembers(data?.members || [])).catch(() => {});
  }, [id]);

  // Demandes d'adhésion en attente (communauté "sur invitation", voir POST /:id/join côté
  // serveur) — visibles seulement par un admin/modérateur.
  const [pending, setPending] = useState<any[]>([]);
  useEffect(() => {
    if (!id || !isModerator) return;
    api.get(`/communities/${id}/members/pending`).then(data => setPending(data?.requests || [])).catch(() => {});
  }, [id, isModerator]);

  const respondPending = (userId: string, approve: boolean) => {
    const path = approve ? `/communities/${id}/members/${userId}/approve` : `/communities/${id}/members/${userId}`;
    const call = approve ? api.put(path, {}) : api.delete(path);
    call.then(() => {
      setPending((prev: any[]) => prev.filter(p => p.id !== userId));
      if (approve) {
        loadCommunity();
        api.get(`/communities/${id}/members`).then(data => setMembers(data?.members || [])).catch(() => {});
      }
    }).catch(() => {});
  };

  useEffect(() => {
    if (!community?.conversation_id || !token) return;
    const socket = getSocket(token);
    socket.emit("join_conversation", community.conversation_id);
    socket.emit("mark_read", community.conversation_id);
    api.get(`/messages/${community.conversation_id}`).then(data => {
      if (data && Array.isArray(data.messages)) setMessages(data.messages);
    }).catch(() => {});

    const onNewMessage = ({ conversationId, message }: any) => {
      if (conversationId === community.conversation_id) setMessages(prev => [...prev, message]);
    };
    socket.on("new_message", onNewMessage);
    return () => { socket.off("new_message", onNewMessage); };
  }, [community?.conversation_id, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = () => {
    if (!inputText.trim() || !community?.conversation_id || !token) return;
    getSocket(token).emit("send_message", { conversationId: community.conversation_id, content: inputText.trim() });
    setInputText("");
  };

  const joinCommunity = () => {
    if (!id) return;
    api.post(`/communities/${id}/join`, {}).then(() => loadCommunity()).catch(() => {});
  };

  const leaveCommunity = () => {
    if (!id || isSoleAdmin) return;
    api.post(`/communities/${id}/leave`, {}).then(data => {
      if (data?.error) { alert(data.error); return; }
      loadCommunity();
    }).catch(() => alert("Erreur de connexion"));
  };

  // Supprimer la communauté — admin uniquement, action destructive donc confirmation native
  // (même convention que le reste de la page, ex. removeMember plus bas).
  const deleteCommunity = () => {
    if (!id || !isAdmin) return;
    if (!window.confirm("Supprimer cette communauté ? Action irréversible, tous les membres perdront l'accès à son contenu et à sa discussion.")) return;
    setDeletingCommunity(true);
    api.delete(`/communities/${id}`).then(data => {
      if (data?.error) { alert(data.error); setDeletingCommunity(false); return; }
      navigate("/");
    }).catch(() => { alert("Erreur de connexion"); setDeletingCommunity(false); });
  };

  const startEditingInfo = () => {
    setEditName(community?.name || "");
    setEditDescription(community?.description || "");
    setEditCategory(community?.category || "");
    setEditingInfo(true);
  };

  const saveCommunityInfo = () => {
    if (!id) return;
    setSavingInfo(true);
    api.put(`/communities/${id}`, { name: editName, description: editDescription, category: editCategory })
      .then(data => {
        if (data?.community) { setCommunity(data.community); setEditingInfo(false); }
        else alert("Erreur lors de la mise à jour");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSavingInfo(false));
  };

  // Changer la photo d'une communauté déjà créée — jusqu'ici uniquement fixée à la création,
  // aucun moyen d'y revenir (l'édition inline "Infos" ne touche que nom/catégorie/description).
  // PUT /communities/:id accepte déjà cover_url (whitelisté côté serveur), seule l'UI manquait.
  const changeCoverPhoto = async (file: File) => {
    if (!id || !isAdmin) return;
    setUploadingCover(true);
    try {
      const url = await uploadImage(file, "community");
      const data = await api.put(`/communities/${id}`, { cover_url: url });
      if (data?.community) setCommunity(data.community);
      else alert("Erreur lors de la mise à jour");
    } catch {
      alert("Erreur de connexion");
    }
    setUploadingCover(false);
  };

  const changeMemberRole = (memberId: string, role: string) => {
    if (!id) return;
    api.put(`/communities/${id}/members/${memberId}/role`, { role })
      .then(data => {
        if (data?.error) { alert(data.error); return; }
        setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role } : m));
      })
      .catch(() => alert("Erreur de connexion"));
  };

  const removeMember = (memberId: string, memberName: string) => {
    if (!id) return;
    if (!window.confirm(`Exclure ${memberName} de la communauté ?`)) return;
    api.delete(`/communities/${id}/members/${memberId}`)
      .then(data => {
        if (data?.error) { alert(data.error); return; }
        setMembers(prev => prev.filter(m => m.id !== memberId));
        setCommunity((c: any) => ({ ...c, members_count: Math.max(0, (c.members_count || 1) - 1) }));
      })
      .catch(() => alert("Erreur de connexion"));
  };

  const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, padding: 20, marginBottom: 12, border: "1px solid #F0F0F0" };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Chargement...</div>;
  if (notFound || !community) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Communauté introuvable.</div>;

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "calc(100vh - 60px)", overflow: isMobile ? "visible" : "hidden" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* COVER */}
        <div style={{ height: 180, background: community.cover_url ? `url(${community.cover_url}) center/cover` : "linear-gradient(135deg, #0a0a0a, #0a0a0a)", position: "relative", flexShrink: 0 }}>
          <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.4)" }} />

          {isAdmin && (
            <label style={{ position: "absolute", inset: 0, cursor: uploadingCover ? "default" : "pointer" }}>
              <input type="file" accept="image/*" disabled={uploadingCover} style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) changeCoverPhoto(f); e.target.value = ""; }} />
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }}
                onMouseOver={e => (e.currentTarget.style.opacity = "1")}
                onMouseOut={e => (e.currentTarget.style.opacity = "0")}>
                <div style={{ backgroundColor: "rgba(0,0,0,0.6)", borderRadius: 20, padding: "8px 16px" }}>
                  <span style={{ color: "white", fontSize: 13, fontWeight: 600 }}>{uploadingCover ? "Envoi..." : "Changer la photo"}</span>
                </div>
              </div>
            </label>
          )}

          <div style={{ position: "absolute", top: 16, left: 20 }}>
            {community.category && (
              <div style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "4px 12px", border: "0.5px solid rgba(255,255,255,0.4)", display: "inline-block", marginBottom: 8 }}>
                <span style={{ color: "white", fontSize: 11, fontWeight: 600 }}>{community.category}</span>
              </div>
            )}
            <div style={{ color: "white", fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{community.name}</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{community.members_count?.toLocaleString()} membres</div>
          </div>

          {!isMember && (
            <button onClick={joinCommunity} style={{ position: "absolute", bottom: 16, right: 20, display: "flex", alignItems: "center", gap: 6, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              {t(lang,"community_join")||"Rejoindre"}
            </button>
          )}
        </div>

        {/* TABS */}
        <div style={{ display: "flex", backgroundColor: "white", borderBottom: "1px solid #E0E0E0", flexShrink: 0 }}>
          {TABS_KEYS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flex: 1, padding: "13px 8px", border: "none", cursor: "pointer",
              backgroundColor: "transparent",
              borderBottom: activeTab === tab ? "2px solid #0a0a0a" : "2px solid transparent",
              color: activeTab === tab ? "#0a0a0a" : "#999",
              fontWeight: activeTab === tab ? 700 : 400, fontSize: 13,
            }}>
              {tab === "Chat" ? (t(lang,"community_chat")||"Chat") :
               tab === "Événements" ? (t(lang,"community_events")||"Événements") :
               tab === "Membres" ? (t(lang,"community_members")||"Membres") :
               (t(lang,"community_infos")||"Infos")}
            </button>
          ))}
        </div>

        {/* CONTENU */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

          {activeTab === "Chat" && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {!isMember ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 32 }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LockIcon size={24} color="#999" />
                  </div>
                  <p style={{ fontSize: 15, color: "#999", textAlign: "center" }}>Rejoignez la communauté pour accéder au chat</p>
                  <button onClick={joinCommunity} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "12px 28px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>{t(lang,"community_join")||"Rejoindre"}</button>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
                    {messages.length === 0 && <div style={{ textAlign: "center", color: "#999", fontSize: 13, marginTop: 40 }}>Aucun message pour le moment</div>}
                    {messages.map((msg, index) => {
                      const isMe = msg.user_id === user?.id;
                      const showAvatar = !isMe && (index === 0 || messages[index - 1].user_id !== msg.user_id);
                      return (
                        <div key={msg.id} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: 6, alignItems: "flex-end", gap: 8 }}>
                          {!isMe && (
                            <div style={{ width: 32, flexShrink: 0 }}>
                              {showAvatar && (
                                <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: colorFromId(msg.user_id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 700 }}>{initials(msg.user_name)}</div>
                              )}
                            </div>
                          )}
                          <div style={{ maxWidth: "60%", display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
                            {showAvatar && !isMe && <span style={{ fontSize: 11, color: "#999", fontWeight: 600, marginBottom: 3, marginLeft: 4 }}>{msg.user_name}</span>}
                            <div style={{ backgroundColor: isMe ? "#0a0a0a" : "white", color: isMe ? "white" : "#0a0a0a", borderRadius: 18, borderBottomRightRadius: isMe ? 4 : 18, borderBottomLeftRadius: isMe ? 18 : 4, padding: "9px 14px", fontSize: 14, lineHeight: 1.5, border: isMe ? "none" : "1px solid #F0F0F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                              {msg.content}
                            </div>
                            <span style={{ fontSize: 10, color: "#bbb", marginTop: 3, marginLeft: 4, marginRight: 4 }}>{new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderTop: "1px solid #F0F0F0", backgroundColor: "white" }}>
                    <input value={inputText} onChange={e => setInputText(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} placeholder="Message..." style={{ flex: 1, padding: "10px 16px", borderRadius: 24, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", backgroundColor: "#F8F8F8" }} />
                    <button onClick={sendMessage} disabled={!inputText.trim()} style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: inputText.trim() ? "#0a0a0a" : "#E0E0E0", border: "none", cursor: inputText.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 18, fontWeight: 700, flexShrink: 0 }}>
                      →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "Événements" && (
            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>Événements actifs</span>
                  {isMember && (
                    <button
                      onClick={() => navigate(isAdmin ? `/create?community_id=${id}&community_name=${encodeURIComponent(community.name)}` : "/create")}
                      style={{ color: "#0a0a0a", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                    >
                      + Créer
                    </button>
                  )}
                </div>
                {events.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>Aucun événement pour le moment</div>}
                {events.map(event => (
                  <div key={event.id} style={{ borderRadius: 14, overflow: "hidden", marginBottom: 14, border: "1px solid #F0F0F0" }}>
                    <div style={{ height: 140, background: event.cover_url ? `url(${event.cover_url}) center/cover` : "linear-gradient(135deg, #1a1a1a, #0a0a0a)", position: "relative", padding: 12, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "4px 10px", color: "white", fontSize: 12, fontWeight: 600 }}>{(event.is_free || Number(event.price) === 0) ? "Gratuit" : `${event.price}€/pers`}</span>
                        {event.max_places && (
                          <div style={{ backgroundColor: "rgba(0,0,0,0.4)", borderRadius: 20, padding: "4px 10px" }}>
                            <span style={{ color: "white", fontSize: 11 }}>{event.max_places - (event.current_places || 0)} places</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ color: "white", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{event.title}</div>
                        {event.event_date && (
                          <div>
                            <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 11 }}>{new Date(event.event_date).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, padding: 12, backgroundColor: "white" }}>
                      <button onClick={() => api.post(`/publications/${event.id}/participate`, {}).catch(() => {})} style={{ flex: 1, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Participer</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "Membres" && (
            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              {isModerator && pending.length > 0 && (
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Demandes en attente ({pending.length})</div>
                  {pending.map((p: any) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                      <Avatar id={p.id} name={p.name} uri={p.avatar_url} size={36} />
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                      <button onClick={() => respondPending(p.id, false)} style={{ border: "none", background: "#FDEAEA", color: "#C0392B", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Refuser</button>
                      <button onClick={() => respondPending(p.id, true)} style={{ border: "none", background: "#0a0a0a", color: "white", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Accepter</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{community.members_count?.toLocaleString()} membres</span>
                </div>
                {members.map(member => {
                  const isSelf = member.id === user?.id;
                  return (
                    <div key={member.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #F5F5F5" }}>
                      <div onClick={() => navigate("/profile/" + member.id)} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, cursor: "pointer", minWidth: 0 }}>
                        <Avatar id={member.id} name={member.name} uri={member.avatar_url} size={44} ringKind={vibeRingKind(member.has_real, member.has_active)} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{member.name}</span>
                            {member.role === "admin" && <span style={{ backgroundColor: "#F0F0F0", color: "#333333", fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "2px 7px" }}>{t(lang,"community_admin")||"Admin"}</span>}
                            {member.role === "moderator" && <span style={{ backgroundColor: "#F0F0F0", color: "#666", fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "2px 7px" }}>Modérateur</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "#0a0a0a" }}>★ {formatScore(member.score)} / 10</div>
                        </div>
                      </div>
                      {isAdmin && !isSelf && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          <select value={member.role} onChange={e => changeMemberRole(member.id, e.target.value)} style={{ fontSize: 12, padding: "6px 8px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#555", cursor: "pointer" }}>
                            <option value="member">Membre</option>
                            <option value="moderator">Modérateur</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button onClick={() => removeMember(member.id, member.name)} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", color: "#666", cursor: "pointer" }}>Exclure</button>
                        </div>
                      )}
                      {!isAdmin && isModerator && !isSelf && member.role !== "admin" && (
                        <button onClick={() => removeMember(member.id, member.name)} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", color: "#666", cursor: "pointer", flexShrink: 0 }}>Exclure</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "Infos" && (
            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>À propos</span>
                  {isAdmin && !editingInfo && (
                    <button onClick={startEditingInfo} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>Gérer</button>
                  )}
                </div>
                {editingInfo ? (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase" }}>Nom</label>
                    <input value={editName} onChange={e => setEditName(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "#F8F8F8" }} />
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase" }}>Catégorie</label>
                    <input value={editCategory} onChange={e => setEditCategory(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "#F8F8F8" }} />
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase" }}>Description</label>
                    <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "#F8F8F8", resize: "none" as const }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setEditingInfo(false)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Annuler</button>
                      <button onClick={saveCommunityInfo} disabled={savingInfo || !editName.trim()} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: savingInfo || !editName.trim() ? 0.6 : 1 }}>{savingInfo ? "..." : "Enregistrer"}</button>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.8, margin: 0 }}>{community.description}</p>
                )}
              </div>
              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Informations</div>
                {[
                  `Communauté ${community.adhesion === "public" ? "publique" : "sur invitation"}`,
                  `${community.members_count?.toLocaleString()} membres`,
                  `Créée le ${new Date(community.created_at).toLocaleDateString("fr-FR")}`,
                  ...(community.admin_names?.length ? [`Administrée par ${community.admin_names.join(", ")}`] : []),
                ].map((text, i) => (
                  <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                    <span style={{ fontSize: 13 }}>{text}</span>
                  </div>
                ))}
              </div>
              {/* Un admin voit désormais ce bouton lui aussi (avant : totalement absent pour un
                  admin, quel que soit le nombre d'autres admins) — juste désactivé tant qu'il
                  est le seul admin, avec l'explication du garde-fou serveur juste en dessous. */}
              {isMember && (
                <button onClick={leaveCommunity} disabled={isSoleAdmin} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "0.5px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: isSoleAdmin ? "default" : "pointer", fontSize: 14, color: "#0a0a0a", fontWeight: 600, opacity: isSoleAdmin ? 0.5 : 1 }}>
                  Quitter la communauté
                </button>
              )}
              {isSoleAdmin && (
                <div style={{ fontSize: 11.5, color: "#999", textAlign: "center", marginTop: 8 }}>
                  Désignez un autre admin (onglet Membres) avant de pouvoir quitter
                </div>
              )}
              {isAdmin && (
                <button onClick={deleteCommunity} disabled={deletingCommunity} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "1.5px solid #c0392b", backgroundColor: "transparent", cursor: "pointer", fontSize: 14, color: "#c0392b", fontWeight: 600, marginTop: 10, opacity: deletingCommunity ? 0.6 : 1 }}>
                  {deletingCommunity ? "..." : "Supprimer la communauté"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SIDEBAR */}
      <div style={{ display: isMobile ? "none" : "block", width: 280, flexShrink: 0, overflowY: "auto", padding: "20px 20px 20px 0", borderLeft: "1px solid #F0F0F0" }}>
        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, marginBottom: 12, border: "1px solid #F0F0F0" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Membres ({members.length})</div>
          {members.slice(0, 8).map(member => (
            <div key={member.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #F8F8F8", cursor: "pointer" }} onClick={() => navigate("/profile/" + member.id)}>
              <Avatar id={member.id} name={member.name} uri={member.avatar_url} size={36} ringKind={vibeRingKind(member.has_real, member.has_active)} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{member.name}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, border: "1px solid #F0F0F0" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>À propos</div>
          <div style={{ fontSize: 13, color: "#666", lineHeight: 1.7, marginBottom: 12 }}>{(community.description || "").slice(0, 100)}{community.description?.length > 100 ? "..." : ""}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: "#0a0a0a" }}>{community.members_count?.toLocaleString()}</span>
            <span style={{ fontSize: 12, color: "#999" }}>membres</span>
          </div>
          {!isMember && (
            <button onClick={joinCommunity} style={{ width: "100%", backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 12, padding: "10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              Rejoindre
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
